import { useState } from "react";

import {
  CustomizeDomainState,
  ScriptModalState,
  UpdateItemData,
} from "../types";
import { ProjectLandingGridListingStates } from "../types";
import useApi from "@/hooks/useApi";
import {
  E_LANDING_PAGE_TYPE,
  ILandingPage,
} from "@/utilities/types/LandingPage";
import { IResponseData } from "@/utilities/types/requests";
import LandingPagesRepository from "@/utilities/repositories/LandingPages";
import { useDnsGuideModal } from "../../create-update/hooks/useDnsGuideModal";

interface useLdpActionModalsProps {
  onRefresh: () => void; // To refresh the data
}

/**
 * Hook to manage all action modals in the grid listing
 */
export const useLdpActionModals = ({ onRefresh }: useLdpActionModalsProps) => {
  // State for option modify modal
  const [modifyModal, setModifyModal] =
    useState<ProjectLandingGridListingStates["modifyModal"]>(null);

  // State for scripts modal
  const [scriptsModal, setScriptsModal] = useState<ScriptModalState | null>(
    null
  );

  // State for domain config modal
  const [customizeDomain, setCustomizeDomain] =
    useState<CustomizeDomainState | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Use the new hook to manage DNS Guide Modal
  const {
    isOpen: isDnsGuideOpen,
    guideData: customDomainData,
    handleDomainSubmit,
    closeDnsGuide: handleDnsGuideClose,
    handleClose: handleDnsClose,
  } = useDnsGuideModal();

  const { request: updateLandingPage } = useApi<IResponseData<ILandingPage>>({
    request: LandingPagesRepository.updateLandingPage,
    enableToast: true,
  });

  /**
   * Open domain config modal
   */
  const handleDomainModal = (data: CustomizeDomainState) => {
    setCustomizeDomain(data);
    if (data?.type === E_LANDING_PAGE_TYPE.CUSTOM && data?.customDomain) {
      handleDomainSubmit(
        data?.domain || "",
        data?.type,
        data?.cNameTargetDomain,
        data || undefined,
        data?.cNameTarget
      );
      setIsModalOpen(false);
    } else {
      setIsModalOpen(true);
    }
  };

  /**
   * Close domain config modal
   */
  const handleModalClose = () => {
    setIsModalOpen(false);
    setCustomizeDomain(null);
  };

  /**
   * Handle returning from DNS Guide modal
   */
  const handleBackToDomainModal = () => {
    // Close DNS Guide modal
    handleDnsGuideClose();

    // Restore domain config state
    if (customDomainData?.originalState) {
      setCustomizeDomain(customDomainData.originalState);
    }

    // Reopen domain config modal
    setIsModalOpen(true);
  };

  /**
   * Update landing page
   */
  const onUpdateItem = async (id: string, data: UpdateItemData) => {
    const { domain, type, cNameTargetDomain, cNameTarget } = data;

    const payload: UpdateItemData = {
      headerScript: data?.headerScript,
      bodyScript: data?.bodyScript,
      enquiryScript: data?.enquiryScript,
      useEnquiryScript: data?.useEnquiryScript,
      layout: data?.layout,
      type: type,
      dataScript: data?.dataScript,
    };

    if (type === E_LANDING_PAGE_TYPE.SUBDOMAIN) {
      payload.subDomain = domain;
    }

    if (type === E_LANDING_PAGE_TYPE.CUSTOM) {
      payload.customDomain = domain;
      payload.cNameTargetDomain = cNameTargetDomain;
    }

    try {
      await updateLandingPage(id, payload);

      // Handle after successful update
      handleModalClose();

      // Open DNS Guide if it's a custom domain
      if (type === E_LANDING_PAGE_TYPE.CUSTOM) {
        handleDomainSubmit(
          domain || "",
          type,
          cNameTargetDomain,
          customizeDomain || undefined,
          cNameTarget
        );
      }

      await onRefresh();
    } catch (error) {
      console.log("Failed to update landing page");
    }
  };

  const onRemoveDomain = async (id: string) => {
    // Close DNS Guide modal
    handleDnsGuideClose();

    //remove domain
    await updateLandingPage(id, {
      type: E_LANDING_PAGE_TYPE.SUBDOMAIN,
      customDomain: "",
    });

    // Restore domain config state
    if (customDomainData?.originalState) {
      setCustomizeDomain({
        ...customDomainData.originalState,
        type: E_LANDING_PAGE_TYPE.SUBDOMAIN,
        customDomain: "",
      });
    }

    // Reopen domain config modal
    setIsModalOpen(true);
    onRefresh();
  };

  return {
    modifyModal,
    setModifyModal,
    scriptsModal,
    setScriptsModal,
    customizeDomain,
    isModalOpen,
    isDnsGuideOpen,
    customDomainData,
    handleDomainModal,
    handleModalClose,
    handleDnsGuideClose,
    handleBackToDomainModal,
    onUpdateItem,
    onRemoveDomain,
    handleDomainSubmit,
    setIsModalOpen,
  };
};
